/**
 * 프론트 공통 날짜/시각 표시 — **전부 Asia/Seoul(KST) 기준**.
 * 관리자(`apps/admin/src/lib/datetime.ts`)와 같은 규약이다 — 화면마다 다르게 찍지 않는다.
 *
 * 배경(`apps/api/prisma/TZ_MIGRATION_NOTES.md` 2026-07-29 조사) —
 * **DB 는 instant 를 UTC 로 canonical 저장**하고 API 는 UTC ISO(`…Z`)로 내려준다.
 * 그래서 **표시하는 쪽이 KST 로 바꿔야 한다.**
 *  - raw ISO 를 `slice(0,10)` 하면 UTC 날짜가 그대로 나온다 → KST 00:00~09:00 구간에서 하루 밀린다
 *  - `new Date(v).getFullYear()` 같은 로컬 포맷도 안 된다 → **서버 렌더(SSR)에서는 그 서버의 TZ**
 *    (Cloud Run 기본 UTC)를 따르고, 해외 접속자는 자기 지역 시각을 본다
 *
 * ⚠️ **달력 날짜(`@db.Date`)에는 쓰지 말 것** — 수강종료일·기간처럼 시각이 없는 값은 Prisma 가
 * **UTC 자정**으로 왕복하므로 KST 로 바꾸면 오히려 하루가 밀린다. 그때는 `ymd()` 를 쓴다.
 */
const KST = 'Asia/Seoul';

/**
 * KST 로 자른 날짜 조각(연·월·일·시·분·초)을 꺼낸다.
 *
 * 포맷 결과 **문자열을 쪼개지 않는다** — 로케일이 자리와 구분자를 마음대로 정한다.
 * (`sv-SE` 는 연도를 넣으면 `2026-08-25 00:19` 지만 빼면 `25/08 00:19` 로 뒤집힌다.
 *  실제로 여기서 `NaN.NaN` 이 나왔다 — 2026-08-25.)
 */
function kstParts(v: string | Date | null | undefined) {
  const d = toDate(v);
  if (!d) return null;
  const got = new Intl.DateTimeFormat('en-US', {
    timeZone: KST, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(d);
  const pick = (type: string) => got.find((x) => x.type === type)?.value ?? '';
  // en-US 는 자정을 `24` 로 주는 구현이 있다(hourCycle h24) — 날짜 칸은 이미 맞으므로 시각만 정리한다
  const hour = pick('hour') === '24' ? '00' : pick('hour');
  return { y: pick('year'), m: pick('month'), d: pick('day'), hh: hour, mm: pick('minute'), ss: pick('second') };
}

/** YYYY-MM-DD (KST) — instant(등록일시·결제일시 등) 전용 */
export function fmtDate(v: string | Date | null | undefined): string {
  const p = kstParts(v);
  return p ? `${p.y}-${p.m}-${p.d}` : '-';
}

/** YYYY.MM.DD (KST) — 가이드 주문 목록 표기 */
export const fmtDateDot = (v: string | Date | null | undefined) => {
  const p = kstParts(v);
  return p ? `${p.y}.${p.m}.${p.d}` : '-';
};

/** YYYY-MM-DD HH:mm (KST) */
export function fmtDateTime(v: string | Date | null | undefined): string {
  const p = kstParts(v);
  return p ? `${p.y}-${p.m}-${p.d} ${p.hh}:${p.mm}` : '-';
}

/** YYYY-MM-DD HH:mm:ss (KST) — 현행 `{date}` 토큰처럼 초까지 찍던 자리 */
export function fmtDateTimeSec(v: string | Date | null | undefined): string {
  const p = kstParts(v);
  return p ? `${p.y}-${p.m}-${p.d} ${p.hh}:${p.mm}:${p.ss}` : '-';
}

/** M.D HH:mm (KST) — 상태 줄처럼 짧게 적는 자리 */
export function fmtShortTime(v: string | Date | null | undefined): string {
  const p = kstParts(v);
  return p ? `${Number(p.m)}.${Number(p.d)} ${p.hh}:${p.mm}` : '-';
}

/**
 * **KST 기준 오늘**부터 `n` 일 뒤의 달력 날짜(YYYY-MM-DD).
 * 수강 시작일 후보처럼 "오늘부터" 를 세는 자리는 이걸 쓴다 —
 * `new Date().toISOString().slice(0,10)` 은 KST 00:00~09:00 사이에 **어제**를 준다.
 */
export function kstDayPlus(n: number): string {
  const base = new Date(`${fmtDate(new Date())}T00:00:00Z`);
  base.setUTCDate(base.getUTCDate() + n);
  return base.toISOString().slice(0, 10);
}

/**
 * 달력 날짜(`@db.Date`) 표기 — **KST 변환을 하지 않는다**.
 * Prisma 가 UTC 자정으로 주므로 앞 10자가 곧 그 달력 날짜다(변환하면 하루 밀린다).
 */
export const ymd = (v: string | Date | null | undefined): string => {
  if (!v) return '';
  return typeof v === 'string' ? v.slice(0, 10) : v.toISOString().slice(0, 10);
};

function toDate(v: string | Date | null | undefined): Date | null {
  if (!v) return null;
  const d = v instanceof Date ? v : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}
